import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { GeneralTab } from './tabs/GeneralTab';
import { MembersTab } from './tabs/MembersTab';
import { OutcomesTab } from './tabs/OutcomesTab';
import { BillingTab } from './tabs/BillingTab';
import { CreditsTab } from './tabs/CreditsTab';
import { RecommendationTab } from './tabs/RecommendationTab';
import { AvailabilityTab } from './tabs/AvailabilityTab';
import { MyAvailabilityTab } from './tabs/MyAvailabilityTab';
import { OrganizationTab } from './tabs/OrganizationTab';
import { resolveReturnTo } from '../../utils/billingReturn';
import { hasCentralAdminClaims, tokenSubject } from '../../utils/adminAccess';
import { BrandBackdrop } from '../../components/BrandBackdrop';
import logo from '../../assets/logo.svg';

export type SettingsTab =
  | 'general'
  | 'members'
  | 'availability'
  | 'my-availability'
  | 'outcomes'
  | 'billing'
  | 'credits'
  | 'recommendation'
  | 'organization';

const VALID_TABS: SettingsTab[] = [
  'general',
  'members',
  'availability',
  'my-availability',
  'outcomes',
  'billing',
  'credits',
  'recommendation',
  'organization',
];

interface TabConfig {
  key: SettingsTab;
  label: string;
  icon: JSX.Element;
}

const tabs: TabConfig[] = [
  {
    key: 'general',
    label: 'General',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    // ADR-0050. User-scoped, unlike its neighbours: an organization spans
    // workspaces, so it is described once rather than per workspace.
    key: 'organization',
    label: 'Organization',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
      </svg>
    ),
  },
  {
    key: 'members',
    label: 'Members',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
  {
    // MEMBER self-service. Deliberately a separate entry from the administrator
    // "Availability" tab below: same domain, same endpoints, same validator —
    // the only difference is that this one targets the signed-in member's own
    // membership. It is NOT appointment/customer scheduling, which is a
    // different bounded context that merely shares the word "schedule".
    key: 'my-availability',
    label: 'My Work Availability',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    // Central Availability — workspace attendance policy + member working hours.
    // Owner/admin managed; the API enforces it and the tab reports it.
    key: 'availability',
    label: 'Availability',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'outcomes',
    label: 'Outcomes',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    key: 'credits',
    label: 'Credits',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    // PR-D7 — advisory capacity recommendation surface. Read-only;
    // shows the latest recommendation computed by the D7 scheduler
    // (or falls back to on-demand compute via the D5b endpoint).
    key: 'recommendation',
    label: 'Recommendation',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const TYPE_LABELS: Record<string, string> = {
  personal: 'Personal',
  team: 'Team',
  enterprise: 'Enterprise',
};

interface WorkspaceInfo {
  id: string;
  name: string;
  type: 'personal' | 'team' | 'enterprise';
  status: string;
}

/**
 * Workspace Settings page
 *
 * Central-hosted settings with workspace-scoped routing.
 * Routes: /workspace/:workspaceId/settings/:tab
 * Supports returnUrl for back navigation.
 */
export function WorkspaceSettingsPage() {
  const { workspaceId: urlWorkspaceId, tab: urlTab, action: urlAction } = useParams<{
    workspaceId?: string;
    tab?: string;
    action?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<WorkspaceInfo | null>(null);
  // The caller's DB role in this workspace (GET /api/workspaces/:id `membership.role`),
  // so tabs can offer only what Central allows.
  const [callerRole, setCallerRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Set when Central itself failed (bridge 5xx) — the error screen then offers a retry.
  const [unavailable, setUnavailable] = useState(false);
  // R1 (V1-CENTRAL-LOGIN): a stored WORKSPACE token is never trusted on load —
  // the session bridge confirms the live Central session first (and mints a
  // fresh token). Trusting it let settings keep working for the token's whole
  // lifetime after a suite logout had ended the session (jwtAuth is
  // stateless). Only an admin-claim token from the /admin/login console keeps
  // its previous behaviour, and not when arriving from an external app
  // (return_to/returnUrl), which has always forced the bridge.
  const hasExternalReturn = searchParams.has('return_to') || searchParams.has('returnUrl');
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem('admin_token');
    return !hasExternalReturn && hasCentralAdminClaims(stored) ? stored : null;
  });

  // Map URL tab names to canonical settings tabs.
  // BrandTalk uses: /settings/profile, /settings/workspace, /settings/workspace/transfer
  // Central uses: general, members, outcomes, billing, credits
  const TAB_ALIASES: Record<string, SettingsTab> = {
    profile: 'general',
    workspace: 'general',
    account: 'general',
  };

  const resolvedTab = urlTab
    ? TAB_ALIASES[urlTab] || (VALID_TABS.includes(urlTab as SettingsTab) ? urlTab as SettingsTab : 'general')
    : (searchParams.get('tab') as SettingsTab) || 'general';
  const activeTab: SettingsTab = resolvedTab;
  const returnUrl = searchParams.get('returnUrl') || searchParams.get('return_to') || '';

  // Load workspace info and handle routing
  useEffect(() => {
    async function loadWorkspace() {
      try {
        // If no token, attempt session bridge (cookie → token)
        let authToken = token;
        if (!authToken) {
          const bridgeRes = await fetch('/api/auth/session/bridge', {
            credentials: 'include',
          });
          if (bridgeRes.ok) {
            const bridgeData = await bridgeRes.json();
            // Multiple workspaces and no current selection: the bridge does
            // NOT bind an arbitrary (oldest) workspace — it asks us to route
            // the user to the workspace picker instead of silently editing the
            // wrong workspace.
            if (bridgeData.needsWorkspaceSelection || !bridgeData.accessToken) {
              const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
              // Replace, not push: Back must not land here only to bounce again.
              window.location.replace(`/auth/workspace?continue=${returnTo}`);
              return;
            }
            authToken = bridgeData.accessToken;
            localStorage.setItem('admin_token', authToken!);
            setToken(authToken);
            // Don't continue — setToken will re-trigger this effect with the token set
            return;
          } else if (bridgeRes.status === 401) {
            // No session — redirect to OIDC login, then return here. Replace,
            // not push: Back must not land here only to bounce again. A stored
            // WORKSPACE token belongs to the session that just ended: drop it
            // (an admin console token is left alone).
            const stored = localStorage.getItem('admin_token');
            if (stored && !hasCentralAdminClaims(stored)) localStorage.removeItem('admin_token');
            const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.replace(`/auth/login?continue=${returnTo}`);
            return;
          } else {
            // 5xx / unexpected: Central is unavailable, NOT signed out. Sending
            // a signed-in user to sign-in here looped (sign in → back here →
            // bridge still failing → sign in). Contract §1.3: unavailable + retry.
            setUnavailable(true);
            setError('LevelUp is temporarily unavailable. Please try again in a moment.');
            setIsLoading(false);
            return;
          }
        }

        let wsId = urlWorkspaceId;

        // Legacy id-less route (no workspace ID in the URL): resolve the
        // user's CURRENT authoritative workspace from the session — NOT
        // workspaces[0] (arbitrary/oldest). /api/workspace/current is the
        // canonical server-side "which workspace am I in" rule, so the id-less
        // path lands on the same workspace the user is actually active in
        // (Defect B: no silent fallback to the oldest/personal workspace).
        if (!wsId) {
          // HOME-01: an explicit `?workspaceId=` (e.g. an app linking to
          // /settings/billing?workspaceId=…) names the workspace. It is
          // honoured by redirecting to the workspace-scoped route, which
          // validates membership itself (non-member → "not found or access
          // denied") — never a silent swap to a different workspace.
          const requestedWs = searchParams.get('workspaceId');
          if (requestedWs && /^[A-Za-z0-9_-]{1,64}$/.test(requestedWs)) {
            wsId = requestedWs;
          } else {
            const currentRes = await fetch('/api/workspace/current', {
              credentials: 'include',
              headers: { Authorization: `Bearer ${authToken}` },
            });
            if (currentRes.ok) {
              const currentData = await currentRes.json();
              wsId = currentData.workspace?.id;
            }
          }

          if (wsId) {
            // HOME-01: the PATH tab (/settings/billing) wins over the legacy
            // `?tab=` query, and a path action (/settings/workspace/transfer)
            // is kept. Previously only `?tab=` was read, so /settings/billing
            // landed on General. Segments are restricted to [a-z-] so nothing
            // but a tab/action name can be spliced into the path.
            const SEGMENT = /^[a-z][a-z-]{0,39}$/;
            const rawTab = urlTab || searchParams.get('tab') || 'general';
            const tab = SEGMENT.test(rawTab) ? rawTab : 'general';
            const action = urlAction && SEGMENT.test(urlAction) ? urlAction : '';
            const tabPath = action ? `/${tab}/${action}` : tab === 'general' ? '' : `/${tab}`;
            // Every other query param (return_to/returnUrl, view=plans, app,
            // billing, …) is carried over unchanged.
            const carried = new URLSearchParams(searchParams);
            carried.delete('tab');
            carried.delete('workspaceId');
            const query = carried.toString();
            navigate(`/workspace/${wsId}/settings${tabPath}${query ? `?${query}` : ''}`, { replace: true });
            return;
          }

          // No workspace bound to the session — route the user to choose one
          // rather than silently editing an arbitrary/oldest workspace.
          setError('No active workspace selected. Please choose a workspace to edit.');
          setIsLoading(false);
          return;
        }

        // Validate workspace access by fetching workspace details
        const wsRes = await fetch(`/api/workspaces/${wsId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!wsRes.ok) {
          if (wsRes.status === 401) {
            // Token expired — clear and retry via bridge
            localStorage.removeItem('admin_token');
            setToken(null);
            return;
          }
          if (wsRes.status === 404 || wsRes.status === 403) {
            setError('Workspace not found or access denied');
          } else {
            setError('Failed to load workspace');
          }
          setIsLoading(false);
          return;
        }

        const wsData = await wsRes.json();
        setWorkspace(wsData.workspace);
        setCallerRole(typeof wsData?.membership?.role === 'string' ? wsData.membership.role : null);
        setIsLoading(false);
      } catch {
        // Network failure: retryable, never a sign-in.
        setUnavailable(true);
        setError('Failed to load workspace');
        setIsLoading(false);
      }
    }

    loadWorkspace();
  // urlTab / urlAction are read only by the id-less redirect, which runs once
  // per id-less URL (it navigates away); listing them would refetch the
  // workspace on every tab switch of the workspace-scoped route.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlWorkspaceId, navigate, searchParams, returnUrl, token]);

  // Phones: the tabs live in a left drawer opened from the menu button.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerCloseRef.current?.focus();
    const trigger = menuButtonRef.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      trigger?.focus();
    };
  }, [menuOpen]);

  const handleTabChange = (tab: SettingsTab) => {
    setMenuOpen(false);
    if (!workspace) return;
    const tabPath = tab === 'general' ? '' : `/${tab}`;
    const returnParam = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
    navigate(`/workspace/${workspace.id}/settings${tabPath}${returnParam}`);
  };

  const handleBack = async () => {
    // CEN-BILL-1: `returnUrl` / `return_to` is raw query input, and contract
    // §1.2 sends every app here with one — assigning it to location.href was
    // an open redirect (https://evil…) and DOM XSS (javascript:…) on Central's
    // origin. It is followed only after the same validation /billing/return
    // uses: a Central path via safeContinuePath (origin-qualified), an
    // absolute URL only with redirect-allowlist approval. Anything else falls
    // back to /home.
    const target = returnUrl ? await resolveReturnTo(returnUrl) : null;
    if (target) {
      window.location.href = target;
    } else {
      // The user's own landing page. NOT `/`: that is the admin console, and
      // for anyone without admin claims it is a redirect, not a destination.
      navigate('/home');
    }
  };

  if (isLoading) {
    return (
      <BrandBackdrop>
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="rounded-3xl bg-white px-10 py-8 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-500">Loading settings...</p>
          </div>
        </div>
      </BrandBackdrop>
    );
  }

  if (error) {
    return (
      <BrandBackdrop>
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 ring-1 ring-red-100">
              <svg className="h-7 w-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="font-medium text-gray-900">{error}</p>
            <div className="mt-6 flex items-center justify-center gap-4">
              {unavailable && (
                <button
                  onClick={() => window.location.reload()}
                  className="rounded-xl bg-emerald-600 px-5 py-2.5 font-semibold text-white shadow-lg shadow-emerald-600/25 hover:bg-emerald-700"
                >
                  Try again
                </button>
              )}
              <button onClick={handleBack} className="font-medium text-emerald-700 hover:text-emerald-800">
                Go back
              </button>
            </div>
          </div>
        </div>
      </BrandBackdrop>
    );
  }

  const tabList = (
    <ul className="space-y-1">
      {tabs.map((tab) => {
        const active = activeTab === tab.key;
        return (
          <li key={tab.key}>
            <button
              onClick={() => handleTabChange(tab.key)}
              aria-current={active ? 'page' : undefined}
              className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                active
                  ? 'bg-white/10 text-white ring-1 ring-white/10'
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <span className={active ? 'text-emerald-300' : 'text-slate-500'}>{tab.icon}</span>
              {tab.label}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <BrandBackdrop>
      <header className="mx-auto flex w-full max-w-6xl items-center gap-3 px-4 py-4 sm:px-6 sm:py-6">
        {/* Phones: the settings menu, top left. Back lives inside it. */}
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Open settings menu"
          aria-expanded={menuOpen}
          aria-controls="settings-drawer"
          className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white backdrop-blur transition-colors hover:bg-white/[0.12] focus:outline-none focus:ring-2 focus:ring-emerald-400 lg:hidden"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h10" />
          </svg>
        </button>
        <button
          onClick={handleBack}
          className="hidden h-10 w-10 flex-none items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-white backdrop-blur transition-colors hover:bg-white/[0.12] focus:outline-none focus:ring-2 focus:ring-emerald-400 lg:flex"
          aria-label="Go back"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <img src={logo} alt="LevelUp" className="h-7 drop-shadow sm:h-9" />
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-16 sm:px-6">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-8">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300/90">Workspace Settings</p>
            {workspace && (
              <h1 className="mt-2 break-words text-2xl font-semibold tracking-tight text-white sm:text-4xl">{workspace.name}</h1>
            )}
          </div>
          {workspace && (
            <span className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs font-semibold text-slate-200 backdrop-blur">
              {TYPE_LABELS[workspace.type] ?? workspace.type} workspace
            </span>
          )}
        </div>

        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 lg:grid lg:grid-cols-12">
          {/* From lg up: the side menu. */}
          <nav aria-label="Settings" className="hidden min-w-0 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 p-4 lg:col-span-3 lg:block">
            {tabList}
          </nav>

          {/* Tab content */}
          <section className="min-w-0 bg-slate-50/70 p-3 sm:p-6 lg:col-span-9 lg:p-8">
            {activeTab === 'general' && workspace && (
              <GeneralTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'organization' && (
              // No `workspace &&` guard: the graph is the USER's, so it loads
              // even where a workspace has not resolved. The id is passed only
              // to mark which node is being viewed.
              <OrganizationTab authToken={token!} workspaceId={workspace?.id} />
            )}
            {activeTab === 'members' && workspace && (
              <MembersTab
                workspaceId={workspace.id}
                workspaceType={workspace.type}
                authToken={token!}
                callerRole={callerRole}
                callerUserId={tokenSubject(token)}
              />
            )}
            {activeTab === 'my-availability' && workspace && (
              <MyAvailabilityTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'availability' && workspace && (
              <AvailabilityTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'outcomes' && workspace && (
              <OutcomesTab
                workspaceId={workspace.id}
                authToken={token!}
                workspaceType={workspace.type}
                callerRole={callerRole}
              />
            )}
            {activeTab === 'billing' && workspace && (
              <BillingTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'credits' && workspace && (
              <CreditsTab workspaceId={workspace.id} authToken={token!} />
            )}
            {activeTab === 'recommendation' && workspace && (
              <RecommendationTab workspaceId={workspace.id} authToken={token!} />
            )}
          </section>
        </div>
      </main>

      {menuOpen && (
        <div id="settings-drawer" role="dialog" aria-modal="true" aria-label="Settings menu" className="fixed inset-0 z-50 lg:hidden">
          <div
            aria-hidden="true"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm motion-safe:animate-fade-in"
          />
          <div className="relative flex h-full w-[85%] max-w-xs flex-col overflow-y-auto bg-gradient-to-b from-slate-900 via-slate-900 to-emerald-950 p-4 shadow-2xl shadow-black/60 ring-1 ring-white/10 motion-safe:animate-drawer-in">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  void handleBack();
                }}
                className="flex items-center gap-2 rounded-full bg-white/[0.06] py-2 pl-2.5 pr-4 text-sm font-semibold text-white ring-1 ring-white/10 transition-colors hover:bg-white/[0.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                <svg className="h-4 w-4 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
              <button
                ref={drawerCloseRef}
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close settings menu"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.06] text-slate-200 ring-1 ring-white/10 transition-colors hover:bg-white/[0.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {workspace && (
              <div className="mt-6 border-b border-white/10 pb-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300/90">Workspace settings</p>
                <p className="mt-1.5 break-words text-lg font-semibold text-white">{workspace.name}</p>
                <span className="mt-2 inline-block rounded-full border border-white/15 bg-white/[0.06] px-2.5 py-0.5 text-[11px] font-semibold text-slate-200">
                  {TYPE_LABELS[workspace.type] ?? workspace.type} workspace
                </span>
              </div>
            )}
            <div className="mt-4">{tabList}</div>
          </div>
        </div>
      )}
    </BrandBackdrop>
  );
}
